/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { useResize, animated, useSpring } from '@react-spring/web';
import { ChevronRight, Sparkles } from 'lucide-react';
import { cloneElement, FC, PropsWithChildren, ReactElement, useContext, useEffect, useRef, useState } from 'react';

import StoryStepResult from './StoryStepResult';

import { BlockStep, MutatesVariables, RecordedActionsStep, Step, TargetingStrategy } from '@/commons/data';
import { Story } from '@/commons/protocols';
import { getSelectorKeysFromEscapeHatch } from '@/pages/projects/LiveAgentSession';
import { StepContext } from '@/pages/projects/RunViewer';

type ReplayStepsProps = {
    stepsToRun: Step[];
    stepResultsFromBack: Step[];
    stepResultsFromWebSocket: Step[];
    storyConfig: Story & { variables?: { [key: string]: string } };
    blocksConfig: Story[];
    debugMode?: boolean;
    hidePlaywright?: boolean;
};

export type StepsWithBlockName = Step & { blockName?: string };

const updateStepsResult = (stepsToRun: Step[], stepResults: Step[], blocks: Story[]): StepsWithBlockName[] => {
    return stepsToRun
        .flatMap((step) => {
            if (step.kind === 'block') {
                // blocks are not really following any particular order
                // so we get the latest block with the same slug
                const matchingBlock = blocks
                    .sort((a, b) => b.id - a.id)
                    .find((b) => b.slug === (step as BlockStep).slug);
                if (!matchingBlock) {
                    return step;
                }
                return (matchingBlock?.steps ?? []).map((s) => {
                    return {
                        ...s,
                        blockStoryId: matchingBlock.id,
                    };
                });
            }
            return step;
        })
        ?.map((step, index) => {
            // Reconciling Steps with their results
            const matchingStep: Step | undefined = stepResults.find((s) => s?.rank === index);
            const updatedStep: StepsWithBlockName = {
                ...step,
                rank: index,
                result: matchingStep?.result ?? 'UNKNOWN',
                actions: matchingStep?.actions ?? [],
            };

            // Adding in blocknames
            if (updatedStep.blockStoryId !== undefined) {
                const blockName = blocks.find((s) => s.id === updatedStep.blockStoryId)?.name;
                updatedStep.blockName = blockName;
            }
            return updatedStep;
        });
};

const RunViewerSteps = ({
    stepsToRun,
    stepResultsFromBack,
    stepResultsFromWebSocket,
    blocksConfig,
    storyConfig,
    debugMode,
    hidePlaywright,
}: ReplayStepsProps) => {
    const { runStatus } = useContext(StepContext);
    const [precomputedVariables, setPrecomputedVariables] = useState<{ [key: string]: string }>({});
    const stepsToDisplay: StepsWithBlockName[] = updateStepsResult(
        stepsToRun,
        runStatus === 'canceled' || runStatus === 'running' ? stepResultsFromWebSocket : stepResultsFromBack,
        blocksConfig,
    );
    const variables = storyConfig.variables ?? {};

    const stepElements = [];
    const syntheticSteps = [];

    useEffect(() => {
        const variablesBuffer: Record<string, string> = {};
        for (const step of stepResultsFromBack ?? []) {
            for (const action of step?.actions ?? {}) {
                try {
                    if (action.variables) {
                        for (const [key, value] of Object.entries(action.variables)) {
                            if (value !== undefined) {
                                variablesBuffer[key] = value;
                            }
                        }
                    }
                    if ('variablesAfter' in action && 'variablesBefore' in action) {
                        const variablesAfter = (action as MutatesVariables).variablesAfter;
                        if (variablesAfter && variablesAfter.length > 0) {
                            for (const [key, value] of variablesAfter) {
                                if (value !== undefined) {
                                    variablesBuffer[key] = value;
                                }
                            }
                        }
                    }
                } catch (e) {}
            }
        }
        setPrecomputedVariables(variablesBuffer);
    }, [stepResultsFromBack]);

    for (let i = 0; i < stepsToDisplay?.length; i += 1) {
        if (stepsToDisplay[i].blockStoryId !== undefined) {
            const stepsInBlock = stepsToDisplay.filter((s) => s.blockStoryId === stepsToDisplay[i].blockStoryId);
            const correspondingBlock = blocksConfig.find((b) => b.id === stepsToDisplay[i].blockStoryId);
            const blockName = correspondingBlock?.name;
            const firstIndex = i;
            i += stepsInBlock.length - 1;
            syntheticSteps.push({ ...stepsInBlock[0], substeps: stepsInBlock });
            const step = {
                kind: 'block' as const,
                blockSteps: stepsInBlock.map((blockstep) => {
                    return { ...blockstep, running: false, executed: true };
                }),
                name: blockName,
                rank: firstIndex,
                result: stepsInBlock.every((s) => s.result === 'PASS') ? 'PASS' : ('FAIL' as 'PASS' | 'FAIL'),
                actions: [],
            };
            stepElements.push(
                <StoryStepResult
                    debugMode={debugMode}
                    hidePlaywright={hidePlaywright}
                    step={step}
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    defaultOpen={false}
                    key={`step-or-block-${i}`}
                />,
            );
        } else if (
            stepsToDisplay[i].kind === 'recordedActions' &&
            stepsToDisplay[i].targetingStrategy &&
            stepsToDisplay[i].targetingStrategy !== 'ai' &&
            (stepsToDisplay[i] as RecordedActionsStep).recorded &&
            (stepsToDisplay[i] as RecordedActionsStep).recorded.length === 1
        ) {
            const originalStep = stepsToDisplay[i] as RecordedActionsStep;
            const selectors = originalStep.recorded[0].selectors;
            let target = '';
            if (selectors) {
                const keys = getSelectorKeysFromEscapeHatch(originalStep.targetingStrategy as TargetingStrategy);
                if (selectors[keys[0]]) {
                    target = (selectors[keys[0]] || { selector: '' }).selector;
                }
            }
            const stepToReturn = {
                ...originalStep,
                kind: 'aiAtomic',
                action: originalStep.recorded[0].name,
                value: originalStep.recorded[0].value,
                intent: originalStep.intent,
                target,
            } as Step;
            syntheticSteps.push(stepToReturn);
            stepElements.push(
                <StoryStepResult
                    debugMode={debugMode}
                    hidePlaywright={hidePlaywright}
                    step={stepToReturn}
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    defaultOpen={false}
                    key={`step-or-block-${i}`}
                />,
            );
        } else {
            syntheticSteps.push(stepsToDisplay[i]);
            stepElements.push(
                <StoryStepResult
                    debugMode={debugMode}
                    hidePlaywright={hidePlaywright}
                    step={stepsToDisplay[i]}
                    variables={variables}
                    precomputedVariables={precomputedVariables}
                    defaultOpen={false}
                    key={`step-or-block-${i}`}
                />,
            );
        }
    }

    const groupedSteps = [];

    for (let i = 0; i < stepElements.length; ) {
        if (!syntheticSteps[i].intentHash) {
            groupedSteps.push(stepElements[i]);
            i++;

            continue;
        } else {
            let j = i;
            const stepsWithSameIntentHash = [];
            while (j < stepElements.length && syntheticSteps[j].intentHash === syntheticSteps[i].intentHash) {
                stepsWithSameIntentHash.push(stepElements[j]);
                j++;
            }
            groupedSteps.push(
                <StepGroup
                    icon={<Sparkles />}
                    groupHeader={syntheticSteps[i].intentPrompt}
                    key={`group-${syntheticSteps[i].intentHash}`}
                >
                    {stepsWithSameIntentHash}
                </StepGroup>,
            );
            i = j;
        }
    }

    return <>{groupedSteps}</>;
};

export const StepGroup: FC<
    PropsWithChildren<{
        groupHeader?: string;
        icon?: ReactElement;
    }>
> = ({ children, groupHeader, icon = <Sparkles /> }) => {
    const [isOpen, setIsOpen] = useState(true);
    const content = useRef<HTMLDivElement>(null);

    const { height } = useResize({ container: content });

    const spring = useSpring({
        from: { rotate: '0deg' },
        to: { rotate: isOpen ? '90deg' : '-90deg' },
    });
    const heightSpring = useSpring({
        to: { height: isOpen ? height : 0 },
    });
    return (
        <div>
            <button
                className="text-slate-500 text-subtle gap-x-2 flex items-center w-full text-left p-1 py-2"
                onClick={() => {
                    setIsOpen(!isOpen);
                }}
            >
                {cloneElement(icon, { size: '24px', className: 'text-purple-400 shrink-0' })}
                {groupHeader && <span>{groupHeader}</span>}
                <animated.div className="ms-auto" style={{ ...spring }}>
                    <ChevronRight size="14px" />
                </animated.div>
            </button>

            <animated.div
                style={{
                    ...heightSpring,
                    overflow: 'hidden',
                }}
            >
                <animated.div ref={content}>
                    <div className="ps-4 pb-1 flex flex-col">{children}</div>
                </animated.div>
            </animated.div>
        </div>
    );
};

export default RunViewerSteps;
